/**
 * The ONE place the app talks to a push transport.
 *
 * See docs/notifications/NOTIFICATIONS_BUILD_SPEC.md §3. Two channels sit
 * behind this module and the rest of the app must not know which is in play:
 *
 *   · native (iOS/Android app) — FCM, via `@capacitor-firebase/messaging`
 *   · web (installed PWA or a browser tab) — standard Web Push (VAPID)
 *
 * The branch is `Capacitor.isNativePlatform()` and it is taken HERE, once. A
 * native app must never register a web subscription even though the Android
 * WebView would happily give it one, and the website must never reach for a
 * Capacitor plugin that is not there.
 *
 * ── What is built, and what is not ───────────────────────────────────────────
 * THE WEB PATH IS COMPLETE (step 3): permission, subscribe, register,
 * reconcile on every app start, and the tap that opens Home in the right
 * society. `getPushSupport()` is complete on both paths.
 *
 * THE NATIVE PATH IS NOT (step 4). `loadNativeMessaging()` returns null until
 * `@capacitor-firebase/messaging` is installed, and everything native is
 * routed through it, so the app behaves correctly in the apps today — it just
 * registers no device. The one thing that must never happen is a native build
 * taking the web path: that is why every branch here asks `isNative()` first
 * and not "do I have a PushManager", which the Android WebView answers yes to.
 */
import { Capacitor } from '@capacitor/core';
import API_URL from '../config';
import APP_VERSION from '../version';

/** The four answers the settings screen renders a notice from (spec §2). */
export const SUPPORT = {
  OK: 'ok',
  DENIED: 'denied',
  IOS_NEEDS_INSTALL: 'ios-needs-install',
  UNSUPPORTED: 'unsupported',
};

export const isNative = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    // Capacitor's web shim is always present in this app, but a test that
    // mocks the module shallowly should not take the screen down with it.
    return false;
  }
};

/**
 * An iPhone or iPad, by the only means a web page has.
 *
 * iPadOS reports itself as a Mac — `navigator.platform === 'MacIntel'` — and
 * is told apart from a real Mac only by the touch points. A desktop-mode iPad
 * is therefore indistinguishable from a MacBook here, which is the same limit
 * `services/client_platform.py` lives with on the server.
 */
const isIOS = () => {
  const nav = typeof navigator === 'undefined' ? null : navigator;
  if (!nav) return false;
  if (/iPad|iPhone|iPod/.test(nav.userAgent || '')) return true;
  return nav.platform === 'MacIntel' && (nav.maxTouchPoints || 0) > 1;
};

/** Launched from the Home Screen rather than in a browser tab. */
const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia?.('(display-mode: standalone)')?.matches) return true;
  } catch {
    // matchMedia is absent in some test environments and throws on a bad
    // query string in a few old engines. Neither is a reason to fail.
  }
  // Non-standard, iOS only, and the ONLY signal on an older iOS.
  return window.navigator?.standalone === true;
};

const hasWebPushApis = () =>
  typeof navigator !== 'undefined' &&
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

/**
 * Can this device be sent a notification, and if not, what should the user be
 * told? Spec §3.3. The settings screen turns the answer into at most one
 * `.info-note` and decides whether the master switch is live.
 *
 * ASYNC, against the spec's signature, which shows it returning a string. It
 * cannot be synchronous: the native answer comes from the plugin's
 * `checkPermissions()`, which is a Promise on both platforms. The screen reads
 * it in an effect, so it was going to await something either way.
 */
export const getPushSupport = async () => {
  if (isNative()) return getNativeSupport();

  // ORDER MATTERS. An iPhone in a Safari TAB has no PushManager, so the
  // capability check would call it unsupported and send the user away — when
  // the truth is that it works perfectly once the app is on the Home Screen.
  // Asking "is this an iOS browser that hasn't been installed yet" first is
  // what turns a dead end into the one instruction that fixes it.
  //
  // A future iOS that allows push in a tab needs no change here: it would
  // have the APIs, skip this branch entirely, and fall through to the
  // permission check like every other browser.
  if (!hasWebPushApis()) {
    return isIOS() && !isStandalone()
      ? SUPPORT.IOS_NEEDS_INSTALL
      : SUPPORT.UNSUPPORTED;
  }

  if (Notification.permission === 'denied') return SUPPORT.DENIED;

  // 'granted' and 'default' are both OK: the second one is a question we have
  // not asked yet, and we only ask inside the tap of a switch going on.
  return SUPPORT.OK;
};

const getNativeSupport = async () => {
  const messaging = await loadNativeMessaging();
  if (!messaging) {
    // STEP 4 fills this in. Until the plugin is installed there is nothing to
    // ask, and 'ok' is the right answer for the screen: it leaves the switches
    // live, and the permission prompt is where a refusal gets discovered
    // anyway. Claiming 'unsupported' would disable the feature on the two
    // platforms it matters most on.
    return SUPPORT.OK;
  }
  try {
    const { receive } = await messaging.checkPermissions();
    return receive === 'denied' ? SUPPORT.DENIED : SUPPORT.OK;
  } catch (err) {
    console.warn('[push] could not read native permissions', err);
    return SUPPORT.OK;
  }
};

/**
 * `@capacitor-firebase/messaging` — the FCM plugin, on native only.
 *
 * STILL LOADED AT RUNTIME, and still only on native. The package has a web
 * implementation that would happily mint an FCM token in a browser, which is
 * exactly what decision D1 says must never happen: the website and the PWA go
 * through standard Web Push and our own service worker. Importing it at the
 * top of the file would also pull the Firebase messaging SDK into the website's
 * bundle for the benefit of two platforms that are not the website.
 *
 * Cached after the first load: `initPush` and every switch tap call it.
 */
let messagingPromise = null;

const loadNativeMessaging = async () => {
  if (!isNative()) return null;
  if (!messagingPromise) {
    messagingPromise = import('@capacitor-firebase/messaging')
      .then((m) => m.FirebaseMessaging)
      .catch((err) => {
        console.warn('[push] the messaging plugin could not be loaded', err);
        return null;
      });
  }
  return messagingPromise;
};

/**
 * Can THIS DEVICE actually be sent to right now?
 *
 * The preference and the subscription are different things, and this is the
 * question the settings screen was not asking. `push_enabled` defaults to TRUE
 * on a new user, so the master switch rendered ON for someone who had never
 * been asked for permission and had no device registered — promising "live
 * updates from your societies" to a browser that would receive nothing, and
 * leaving no tap available to put it right, because the switch already looked
 * on.
 *
 * Returns true/false, or NULL for "cannot tell" — which is the honest answer
 * on a native build until step 4 installs the plugin. The caller falls back to
 * the stored preference there rather than showing a switch that fights itself.
 */
export const isDeviceSubscribed = async () => {
  if (isNative()) {
    const messaging = await loadNativeMessaging();
    if (!messaging) return null; // step 4
    try {
      const { receive } = await messaging.checkPermissions();
      return receive === 'granted';
    } catch {
      return null;
    }
  }

  if (!hasWebPushApis() || Notification.permission !== 'granted') return false;
  try {
    const reg = await serviceWorkerReady();
    if (!reg) return false;
    // Permission alone is not enough: browsers drop subscriptions silently,
    // and a granted permission with no subscription receives nothing.
    return !!(await reg.pushManager.getSubscription());
  } catch (err) {
    console.warn('[push] could not read this device\'s subscription', err);
    return false;
  }
};

/**
 * Ask the OS, and record this device if it says yes.
 *
 * MUST BE CALLED SYNCHRONOUSLY FROM THE TAP that turned a switch on — no
 * `await` may come first. iOS web and Safari reject a permission prompt that
 * is not attributable to a user gesture, and the rejection is silent: the
 * promise resolves 'default' and the switch simply never works. Never call it
 * at launch.
 *
 * Resolves to whether this device may now be sent to, which is what the switch
 * needs to decide where to settle.
 *
 * @param api `authenticatedFetch` from `useApi` — injected rather than imported
 *            so this module stays a plain, testable module rather than a hook.
 */
export const enablePushOnThisDevice = async (api) => {
  const granted = await requestPermission();
  if (!granted) return false;

  // THE PERMISSION IS THE ANSWER; the registration is a separate job that can
  // fail on its own. If subscribing or POSTing falls over, the user's
  // PREFERENCE is still on and still governs their other devices — so the
  // switch must settle ON. Letting this reject instead left the switch dead
  // with nothing on screen to explain it.
  try {
    await registerThisDevice(api);
  } catch (err) {
    console.warn('[push] permission granted, but this device could not be registered', err);
  }
  return true;
};

/** Release this device's handle. The user's preference is saved separately. */
export const disablePushOnThisDevice = async (api) => {
  try {
    await unregisterThisDevice(api);
  } catch (err) {
    // Turning it OFF must never fail in the user's face: the preference save
    // is what actually stops the sends.
    console.warn('[push] could not release this device', err);
  }
};

const requestPermission = async () => {
  if (isNative()) {
    const messaging = await loadNativeMessaging();
    if (!messaging) return true; // step 4; see getNativeSupport
    try {
      const { receive } = await messaging.requestPermissions();
      return receive === 'granted';
    } catch (err) {
      console.warn('[push] native permission request failed', err);
      return false;
    }
  }

  if (!hasWebPushApis()) return false;
  try {
    // Already answered? Don't ask again — a second call resolves instantly
    // with the stored answer, but on some engines it also resets the prompt's
    // one-shot budget.
    if (Notification.permission === 'granted') return true;
    if (Notification.permission === 'denied') return false;
    const result = await Notification.requestPermission();
    return result === 'granted';
  } catch (err) {
    console.warn('[push] permission request failed', err);
    return false;
  }
};

/* ── The application server key ───────────────────────────────────────────── */

/**
 * The VAPID public key, build-time first and server second.
 *
 * `REACT_APP_VAPID_PUBLIC_KEY` is baked into the bundle, which is right for
 * the website but wrong for a phone app that froze a copy of an older build —
 * so `/push/vapid-public-key` is the fallback those can reach. Cached for the
 * session: it changes only when the key pair is rotated.
 */
let vapidKeyPromise = null;

const fetchVapidKey = async () => {
  const fromEnv = (process.env.REACT_APP_VAPID_PUBLIC_KEY || '').trim();
  if (fromEnv) return fromEnv;
  try {
    const res = await fetch(`${API_URL}/push/vapid-public-key`);
    if (!res.ok) return '';
    const { public_key: key } = await res.json();
    return (key || '').trim();
  } catch (err) {
    console.warn('[push] could not read the VAPID key', err);
    return '';
  }
};

const getVapidKey = () => {
  if (!vapidKeyPromise) vapidKeyPromise = fetchVapidKey();
  return vapidKeyPromise;
};

/** The key travels as base64url; `subscribe` wants the raw bytes. */
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
};

/* ── The transport ────────────────────────────────────────────────────────── */

/**
 * The app's service worker registration, or null if there isn't one.
 *
 * `navigator.serviceWorker.ready` NEVER REJECTS AND NEVER TIMES OUT — with no
 * registration it simply stays pending for the life of the page. That is not a
 * hypothetical: the dev server deliberately unregisters every worker
 * (`serviceWorkerUpdates.js`), and in production a registration can fail or be
 * cleared by the user mid-session. Awaiting it unguarded hangs the caller
 * forever, which showed up as a settings switch that did nothing after the
 * permission prompt and said nothing about why.
 */
const SW_READY_TIMEOUT_MS = 5000;

const serviceWorkerReady = () => Promise.race([
  navigator.serviceWorker.ready,
  new Promise((resolve) => { setTimeout(() => resolve(null), SW_READY_TIMEOUT_MS); }),
]).catch(() => null);

const postDevice = async (api, body) => {
  const res = await api('/devices', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`/devices responded ${res.status}`);
  return res;
};

/**
 * Subscribe this device and tell the server about it.
 *
 * IDEMPOTENT ON PURPOSE. `POST /devices` upserts on the handle, so calling
 * this on every app start costs one request and repairs a subscription the
 * browser dropped silently — which they do, without warning or event.
 */
const registerThisDevice = async (api) => {
  if (isNative()) {
    const messaging = await loadNativeMessaging();
    if (!messaging) {
      console.info('[push] native device registration lands in build step 4');
      return false;
    }
    const { token } = await messaging.getToken();
    if (!token) return false;
    await postDevice(api, {
      platform: Capacitor.getPlatform(),
      token,
      // WHICH WEB BUNDLE this phone app froze — the same value the login
      // history records, so a stale native build is identifiable from either.
      app_version: APP_VERSION,
    });
    return true;
  }

  if (!hasWebPushApis()) return false;
  const key = await getVapidKey();
  if (!key) {
    console.warn('[push] no VAPID public key configured; cannot subscribe');
    return false;
  }

  // The app's OWN service worker, never a second one.
  const reg = await serviceWorkerReady();
  if (!reg) return false;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      // Required by every engine, and the promise Apple holds us to: see the
      // service worker's `push` handler.
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key),
    });
  }

  await postDevice(api, {
    platform: 'web',
    subscription: sub.toJSON(),
    user_agent: navigator.userAgent,
  });
  return true;
};

const unregisterThisDevice = async (api) => {
  if (isNative()) {
    const messaging = await loadNativeMessaging();
    if (!messaging) {
      console.info('[push] native de-registration lands in build step 4');
      return;
    }
    const { token } = await messaging.getToken();
    await api('/devices', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    return;
  }

  if (!hasWebPushApis()) return;
  const reg = await serviceWorkerReady();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;

  // CAPTURE THE ENDPOINT FIRST: `unsubscribe()` is what the server identifies
  // the row by, and reading it afterwards gives nothing to delete.
  const { endpoint } = sub;
  try {
    await sub.unsubscribe();
  } catch (err) {
    console.warn('[push] unsubscribe failed; removing the row anyway', err);
  }
  await api('/devices', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint }),
  });
};

/* ── Receiving ────────────────────────────────────────────────────────────── */

/** Where a cold start parks the society until the profile has loaded. */
export const PENDING_SOCIETY_KEY = 'pendingNotificationSociety';

const stashPendingSociety = (societyId) => {
  try {
    sessionStorage.setItem(PENDING_SOCIETY_KEY, String(societyId));
  } catch {
    // Private mode. The tap still opens Home; it just opens the society that
    // was already current, which is a far better outcome than throwing here.
  }
};

/**
 * A notification was tapped: switch to its society, then go Home (spec §5.2).
 *
 * The society is applied ONLY if the user is still a member of it — a tap on
 * an old notification from a society they have since left must not strand them
 * on a society they cannot see. In that case it is simply Home, as it is.
 *
 * On a COLD START the tap data arrives before auth has loaded, so there is no
 * membership list to check against yet. The id is stashed and `applyPendingSociety`
 * picks it up once the profile is there.
 */
export const openFromNotification = (data, ctx = {}) => {
  const { navigate, setCurrentSociety, userProfile } = ctx;
  const societyId = Number(data?.society_id);

  if (societyId) {
    if (!userProfile?.societies) {
      stashPendingSociety(societyId);
    } else {
      const society = userProfile.societies.find((s) => Number(s.id) === societyId);
      if (society) setCurrentSociety?.(society);
    }
  }
  navigate?.('/');
};

/**
 * Apply a society parked by a cold start or carried in on `?society=`.
 * Returns the society that was applied, or null.
 */
export const applyPendingSociety = (userProfile, setCurrentSociety, societyId) => {
  let wanted = societyId;
  if (!wanted) {
    try {
      wanted = sessionStorage.getItem(PENDING_SOCIETY_KEY);
    } catch {
      wanted = null;
    }
  }
  if (!wanted || !userProfile?.societies) return null;

  try {
    sessionStorage.removeItem(PENDING_SOCIETY_KEY);
  } catch { /* nothing to clear */ }

  const society = userProfile.societies.find((s) => Number(s.id) === Number(wanted));
  if (!society) return null;
  setCurrentSociety?.(society);
  return society;
};

/**
 * Call once at app start, after sign-in. Reconciles this device's subscription
 * and starts listening for notifications.
 *
 * @param opts.api            authenticatedFetch
 * @param opts.pushEnabled    the user's master preference
 * @param opts.onTap          (data) => void — the app's own openFromNotification
 * @param opts.showPill       (data) => void, OPTIONAL. Step 6. Where it is
 *                            absent a push arriving with the tab in FRONT is
 *                            logged and nothing is shown; background the tab
 *                            to see one until the pill ships.
 * @returns a teardown function.
 */
/**
 * The native half of `initPush` — FCM, iOS and Android.
 *
 * THE DATA IS ON `event.notification.data`, and it is the SAME payload the
 * service worker hands the web path (`push_sender.PushNotification.data()`),
 * so `onTap` and `showPill` cannot tell the two transports apart. That is the
 * point: one tap handler, one pill, two ways in.
 *
 * FCM stringifies every data value in transit, so `society_id` arrives as
 * "7" rather than 7 — `openFromNotification` coerces it with Number(), which
 * is why it must stay the only thing that reads it.
 */
const initNative = ({ api, onTap, showPill }) => {
  const handles = [];

  loadNativeMessaging().then((messaging) => {
    if (!messaging) return;

    createAndroidChannels(messaging);

    // A tap, whether the app was killed, backgrounded or in front.
    handles.push(messaging.addListener('notificationActionPerformed', (event) => {
      onTap?.(event?.notification?.data);
    }));

    // Arrived while the app is OPEN. iOS shows no banner for these
    // (`presentationOptions: []` in capacitor.config.ts), so the pill is the
    // only thing that will show it.
    handles.push(messaging.addListener('notificationReceived', (event) => {
      const data = event?.notification?.data;
      if (showPill) showPill(data);
      else console.info('[push] foreground notification, no pill mounted', data);
    }));

    // FCM tokens ROTATE without warning — on reinstall, on restore from
    // backup, and at the service's own discretion. A token nobody re-registers
    // is a phone that quietly stops being told anything.
    handles.push(messaging.addListener('tokenReceived', () => {
      if (api) registerThisDevice(api).catch(() => {});
    }));
  });

  return () => {
    handles.forEach((h) => Promise.resolve(h).then((l) => l?.remove?.()).catch(() => {}));
  };
};

/**
 * Android notification channels (spec §8).
 *
 * ANDROID 8+ WILL NOT SHOW A NOTIFICATION WHOSE CHANNEL DOES NOT EXIST — it
 * drops it silently, with nothing in logcat worth finding. The server names a
 * channel on every message (`ANDROID_CHANNELS` in push_sender.py), so these
 * three have to be created before the first one can arrive.
 *
 * Three rather than one because a channel is where ANDROID lets the user turn
 * things off: a member who finds birdie alerts too chatty can silence just
 * those in the system settings and keep hearing who won. Creating a channel
 * that already exists is a no-op, so this runs on every start.
 *
 * Importance 5 = HIGH (heads-up); 3 = DEFAULT. Only eagles-and-better are
 * urgent enough to interrupt, and that is already the `urgent` flag the server
 * sets — the channel merely allows it.
 */
const ANDROID_CHANNELS = [
  { id: 'round_activity', name: 'Round starts',
    description: 'When a member of your society tees off', importance: 3 },
  { id: 'round_results', name: 'Round results',
    description: 'Final standings and who won', importance: 3 },
  { id: 'scoring_highlights', name: 'Birdies & eagles',
    description: 'Every birdie or better, as it goes in', importance: 5 },
];

const createAndroidChannels = (messaging) => {
  if (Capacitor.getPlatform() !== 'android') return;
  ANDROID_CHANNELS.forEach((channel) => {
    messaging.createChannel(channel).catch((err) => {
      console.warn(`[push] could not create channel ${channel.id}`, err);
    });
  });
};

export const initPush = (opts = {}) => {
  const { api, pushEnabled, onTap, showPill } = opts;

  const reconcile = async () => {
    if (!pushEnabled || !api) return;
    try {
      if (isNative()) {
        const messaging = await loadNativeMessaging();
        if (!messaging) return;
        const { receive } = await messaging.checkPermissions();
        if (receive === 'granted') await registerThisDevice(api);
        return;
      }
      // Re-POST on every start. Subscriptions are dropped silently by
      // browsers, so "we registered it once" is not a durable fact.
      if (hasWebPushApis() && Notification.permission === 'granted') {
        await registerThisDevice(api);
      }
    } catch (err) {
      console.warn('[push] could not reconcile this device', err);
    }
  };
  reconcile();

  if (isNative()) return initNative({ api, onTap, showPill });
  if (!('serviceWorker' in navigator)) return () => {};

  const onMessage = (event) => {
    const { type, data } = event.data || {};
    if (type === 'PUSH_FOREGROUND') {
      if (showPill) showPill(data);
      else console.info('[push] foreground notification; the pill lands in build step 6', data);
    } else if (type === 'PUSH_OPEN_HOME') {
      onTap?.(data);
    } else if (type === 'PUSH_RESUBSCRIBED') {
      // The browser rotated our subscription and the worker has a new one; it
      // has no token to register it with, so that is this side's job.
      reconcile();
    }
  };

  navigator.serviceWorker.addEventListener('message', onMessage);
  return () => navigator.serviceWorker.removeEventListener('message', onMessage);
};
